import { useCallback, useMemo } from "react"

interface SelectableItem {
  id: string
  disabled?: boolean
  colorSlot?: number
}

export interface SeriesSelection {
  pinColorSlots: (colorSlots: Record<string, number>) => void
}

interface SelectionCallbacks {
  setItems: (itemId: string, defaultDisabled?: boolean) => void
  setOnlyItem: (itemId: string, allItemIds: string[]) => void
  enableItems: (itemIds: string[]) => void
  onAddSeries?: (metricId: string) => void
}

/** Pins colors before each change when seriesSelection is set; otherwise the callbacks pass through. */
export function useSeriesSelection(
  items: SelectableItem[],
  { setItems, setOnlyItem, enableItems, onAddSeries }: SelectionCallbacks,
  seriesSelection: SeriesSelection | undefined,
) {
  const pinColorSlots = seriesSelection?.pinColorSlots

  const selectedItems = useMemo(() => items.filter(item => !item.disabled), [items])
  const plottedMetricIds = useMemo(
    () => (pinColorSlots === undefined ? undefined : new Set(selectedItems.map(item => item.id))),
    [pinColorSlots, selectedItems],
  )

  const pinCurrentColorSlots = useCallback(() => {
    if (!pinColorSlots) return
    pinColorSlots(Object.fromEntries(
      selectedItems.flatMap(item => (item.colorSlot === undefined ? [] : [[item.id, item.colorSlot]])),
    ))
  }, [pinColorSlots, selectedItems])

  const selectionSetItems = useCallback((itemId: string, defaultDisabled?: boolean) => {
    pinCurrentColorSlots()
    setItems(itemId, defaultDisabled)
  }, [pinCurrentColorSlots, setItems])

  const selectionSetOnlyItem = useCallback((itemId: string, allItemIds: string[]) => {
    pinCurrentColorSlots()
    setOnlyItem(itemId, allItemIds)
  }, [pinCurrentColorSlots, setOnlyItem])

  const selectionEnableItems = useCallback((itemIds: string[]) => {
    pinCurrentColorSlots()
    enableItems(itemIds)
  }, [pinCurrentColorSlots, enableItems])

  const selectionOnAddSeries = useCallback((metricId: string) => {
    pinCurrentColorSlots()
    onAddSeries?.(metricId)
  }, [pinCurrentColorSlots, onAddSeries])

  return {
    setItems: selectionSetItems,
    setOnlyItem: selectionSetOnlyItem,
    enableItems: selectionEnableItems,
    onAddSeries: onAddSeries && selectionOnAddSeries,
    plottedMetricIds,
  }
}
